/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { Policy, PolicyAnalytics } from '@amfam/policy/models';
import { PrettyPolicyNum } from '@amfam/policy/pipes';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { PolicyChangeRequestType } from '@amfam/shared/models';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { Option, ValidationService } from '@amfam/ui-kit';

import { ChangeRequest, ChangeRequestPolicies } from '../../models';

@Component({
  selector: 'ds-change-request-mortgage-lender-details',
  templateUrl: './change-request-mortgage-lender-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChangeRequestMortgageLenderDetailsComponent implements OnInit, OnDestroy {
  @Input() policyList: Policy[];
  @Input() propertyPolicies: Policy[];
  @Output() changeRequestLender = new EventEmitter<ChangeRequestPolicies>();
  @Output() cancelEvent = new EventEmitter();
  @Output() valueChangeEvent = new EventEmitter();

  public mortgageForm: UntypedFormGroup;
  public updateOptions: Option[];
  public policyListOptions: Option[] = [];
  public maxCommentsLength = 850;
  private subscription: Subscription = new Subscription();

  constructor(
    private copyService: CopyService,
    private formBuilder: UntypedFormBuilder,
    private analyticsFacade: AnalyticsFacade,
    public roleService: ImpersonateRolesService
  ) {}

  ngOnInit() {
    this.updateOptions = this.copyService.getCopy(
      'policy.thirdPartyInterest.mortgageLenderChangeRequest.options'
    );

    this.propertyPolicies.forEach((policy: Policy, i: number) => {
      this.policyListOptions = [
        ...this.policyListOptions,
        {
          text: policy.riskDescriptionList,
          id: i,
          helpText: [
            {
              text: `Policy #: ${new PrettyPolicyNum().transform(policy.policyNumber)}`
            }
          ],
          code: policy.policyNumber
        }
      ] as Option[];
    });

    this.initForm();

    this.subscription.add(
      this.mortgageForm
        .get('options')
        .valueChanges.pipe(distinctUntilChanged())
        .subscribe(({ id }) => {
          // conditionally add/remove field validators based on option selection
          if (id === 'change') {
            this.analyticsFacade.trackButtonClick(PolicyAnalytics.buttonPoliciesRequestChangeTPI);
            this.mortgageForm.get('removeLender').clearValidators();
            this.mortgageForm.get('removeLender').reset();
            this.mortgageForm.get('addLender').clearValidators();
            this.mortgageForm.get('addLender').reset();
            this.mortgageForm
              .get('currentLender')
              .setValidators(Validators.compose([Validators.required, Validators.minLength(1)]));
            this.mortgageForm
              .get('newLender')
              .setValidators(Validators.compose([Validators.required, Validators.minLength(1)]));
          }

          if (id === 'remove') {
            this.analyticsFacade.trackButtonClick(PolicyAnalytics.buttonPoliciesRequestRemoveTPI);
            this.mortgageForm.get('newLender').clearValidators();
            this.mortgageForm.get('currentLender').clearValidators();
            this.mortgageForm.get('addLender').clearValidators();
            this.mortgageForm.get('newLender').reset();
            this.mortgageForm.get('currentLender').reset();
            this.mortgageForm.get('addLender').reset();
            this.mortgageForm
              .get('removeLender')
              .setValidators(Validators.compose([Validators.required, Validators.minLength(1)]));
          }

          if (id === 'add') {
            this.analyticsFacade.trackButtonClick(PolicyAnalytics.buttonPoliciesRequestAddTPI);
            this.mortgageForm.get('newLender').clearValidators();
            this.mortgageForm.get('currentLender').clearValidators();
            this.mortgageForm.get('removeLender').clearValidators();
            this.mortgageForm.get('newLender').reset();
            this.mortgageForm.get('currentLender').reset();
            this.mortgageForm.get('removeLender').reset();
            this.mortgageForm
              .get('addLender')
              .setValidators(Validators.compose([Validators.required, Validators.minLength(1)]));
          }

          this.mortgageForm.updateValueAndValidity();
        })
    );

    this.subscription.add(
      this.mortgageForm.valueChanges.subscribe(changes => {
        if (!Object.values(changes).every(x => x === null || x === ''))
          this.valueChangeEvent.emit();
      })
    );
  }

  private initForm() {
    this.mortgageForm = this.formBuilder.group({
      selectedPolicy: ['', this.propertyPolicies.length > 1 ? Validators.required : []],
      options: ['', Validators.compose([Validators.required])],
      currentLender: [''],
      newLender: [''],
      removeLender: [''],
      addLender: [''],
      comment: [
        '',
        Validators.compose([
          Validators.maxLength(this.maxCommentsLength),
          ValidationService.textInputValidator
        ])
      ]
    });
  }

  public getControl(controlName: string): AbstractControl[] {
    return (<UntypedFormArray>this.mortgageForm.get(controlName)).controls;
  }

  public submit(): void {
    let changeRequest = new ChangeRequest();

    changeRequest = Object.assign({}, changeRequest, {
      selectedPolicyNumber: this.mortgageForm.controls.selectedPolicy.value.code,
      policyChangeType: PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE,
      requestedChange: this.mortgageForm.controls.options.value.changeType,
      currentLenderName: this.mortgageForm.controls.currentLender.value,
      newLenderName: this.mortgageForm.controls.newLender.value,
      lenderToBeRemoved: this.mortgageForm.controls.removeLender.value,
      comment: this.mortgageForm.controls.comment.value,
      addLenderName: this.mortgageForm.controls.addLender.value
    });

    this.analyticsFacade.trackEvent({
      event: 'change_profile_success',
      profile_change_type: 'lender'
    });

    this.changeRequestLender.emit(changeRequest);
  }

  public cancel(): void {
    this.cancelEvent.emit();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
